/**
 * Brief 80 — BreachReporter modal
 *
 * MLCO/Director-only form to log a breach against an MLCO-owned policy
 * (and optionally a matter reference). Posts to /api/policy/<slug>/breach
 * with required reason. Server dispatches Bonsai Mail notification to the
 * MLCO + Director on success.
 */

import React, { useState } from "react";
import { X, AlertOctagon, AlertCircle } from "lucide-react";
import { authFetch } from "../../config/api";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function BreachReporter({ onClose }) {
  const [slug, setSlug] = useState("aml-policy");
  const [matterRef, setMatterRef] = useState("");
  const [occurredAt, setOccurredAt] = useState(
    new Date().toISOString().slice(0, 16),
  );
  const [severity, setSeverity] = useState("medium");
  const [title, setTitle] = useState("");
  const [descriptionMd, setDescriptionMd] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  async function submit() {
    if (!title || !descriptionMd || !reason) {
      setError("Title, description and reason are all required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await authFetch(`${API}/api/policy/${slug}/breach`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          matter_reference: matterRef || null,
          occurred_at: new Date(occurredAt).toISOString(),
          severity,
          title,
          description_md: descriptionMd,
          reason,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status} — ${await r.text()}`);
      const j = await r.json();
      setSuccess(
        `Breach ${j.breach_id} logged. Notification: ${j.notification_status} (${(j.notification_recipients || []).length} recipients).`,
      );
      setTimeout(onClose, 1800);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div data-testid="breach-modal" className="bg-white rounded shadow-lg w-full max-w-2xl max-h-full overflow-auto">
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold flex items-center gap-2"><AlertOctagon size={16} /> Log a breach</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium" htmlFor="b-slug">Policy</label>
              <select
                id="b-slug"
                data-testid="breach-slug-select"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full mt-1 border rounded p-2"
              >
                <option value="aml-policy">aml-policy</option>
                <option value="governance-policy">governance-policy</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="b-severity">Severity</label>
              <select
                id="b-severity"
                data-testid="breach-severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full mt-1 border rounded p-2"
              >
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="critical">critical</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium" htmlFor="b-matter">Matter reference (optional)</label>
              <input
                id="b-matter"
                data-testid="breach-matter-ref"
                type="text"
                value={matterRef}
                onChange={(e) => setMatterRef(e.target.value)}
                className="w-full mt-1 border rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="b-occurred">Occurred at</label>
              <input
                id="b-occurred"
                data-testid="breach-occurred-at"
                type="datetime-local"
                value={occurredAt}
                onChange={(e) => setOccurredAt(e.target.value)}
                className="w-full mt-1 border rounded p-2"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="b-title">Title</label>
            <input
              id="b-title"
              data-testid="breach-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full mt-1 border rounded p-2"
            />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="b-desc">Description (markdown)</label>
            <textarea
              id="b-desc"
              data-testid="breach-description"
              value={descriptionMd}
              onChange={(e) => setDescriptionMd(e.target.value)}
              rows={4}
              className="w-full mt-1 border rounded p-2 font-mono text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium" htmlFor="b-reason">Reason for the audit log (required)</label>
            <input
              id="b-reason"
              data-testid="breach-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full mt-1 border rounded p-2"
            />
          </div>
          {error && (
            <div data-testid="breach-error" className="text-sm text-red-700 flex items-center gap-1">
              <AlertCircle size={14} /> {error}
            </div>
          )}
          {success && (
            <div data-testid="breach-success" className="text-sm text-green-700">{success}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              data-testid="breach-cancel"
              onClick={onClose}
              className="px-3 py-1.5 text-sm border rounded"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="breach-submit"
              onClick={submit}
              disabled={submitting}
              className="px-3 py-1.5 text-sm border rounded bg-red-700 text-white disabled:opacity-50"
            >
              Log breach
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
